// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — GAAIM Compliance API Hooks
// apps/web/src/lib/api/compliance.ts
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '@forgetrack/api-client';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface GaaimEvent {
  id: string;
  eventType: string;
  subjectType: string;
  subjectId: string;
  complianceLevel: string;
  confidence: number;
  signatureHash: string;
  previousEventHash: string;
  attributions: string; // JSON array
  expertiseUris: string; // JSON array
  payload: string; // JSON object
  createdAt: string;
  promotedAt: string | null;
  promotedByUserId: number | null;
  sourceConversationId?: number | null;
  sourcePipelineRunId?: number | null;
}

export interface GaaimEventsPage {
  items: GaaimEvent[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface GaaimStats {
  totalEvents: number;
  byLevel: { l0: number; l1: number };
  byCategory: { versionControl: number; aiWorkflow: number; supplyChain: number };
  latestEventAt: string | null;
}

export interface ChainStatus {
  chainIntact: boolean;
  totalLinks: number;
  brokenAt: string | null;
}

export interface EventFilters {
  eventType?: string;
  complianceLevel?: string;
  subjectType?: string;
}

// ─── Generic fetch (local copy to avoid circular imports) ──────────────────

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: { message: string; status: number } | null;
  refetch: () => Promise<void>;
}

function useApiFetch<T>(path: string, enabled = true): UseApiState<T> {
  const { createApi } = useApiClient();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<{ message: string; status: number } | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const api = await createApi();
      const response = await api.get<T>(path);
      setData(response.data);
    } catch (err: any) {
      setError({ message: err.response?.data?.error || err.message || 'Request failed', status: err.response?.status || 500 });
    } finally {
      setLoading(false);
    }
  }, [createApi, path]);

  useEffect(() => { if (enabled) fetch(); }, [fetch, enabled]);
  return { data, loading, error, refetch: fetch };
}

// ─── Hooks ─────────────────────────────────────────────────────────────────

export function useGaaimEvents(page: number, filters: EventFilters = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', '50');
  if (filters.eventType) params.set('eventType', filters.eventType);
  if (filters.complianceLevel) params.set('complianceLevel', filters.complianceLevel);
  if (filters.subjectType) params.set('subjectType', filters.subjectType);

  return useApiFetch<GaaimEventsPage>(`/api/v1/gaaim/events?${params.toString()}`);
}

export function useGaaimStats() {
  return useApiFetch<GaaimStats>('/api/v1/gaaim/stats');
}

export function useGaaimChainStatus() {
  return useApiFetch<ChainStatus>('/api/v1/gaaim/chain-status');
}

export function useGaaimPromote() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const promote = useCallback(async (eventId: string, reviewOutcome = 'approved', correctionNotes?: string) => {
    try {
      setLoading(true);
      const api = await createApi();
      await api.post(`/api/v1/gaaim/events/${eventId}/promote`, { reviewOutcome, correctionNotes });
      return true;
    } catch { return false; }
    finally { setLoading(false); }
  }, [createApi]);

  return { promote, loading };
}
